import { MessageDescriptor } from 'react-intl'

import { IntlService } from '../services/intl/IntlService'
import { PushEnvelope, SUPPORTED_SCHEMA_VERSION } from './envelope'
import { pushNotificationMessages } from './PushNotificationMessages'

interface EventDef {
  descriptor: MessageDescriptor
  /**
   * Every ICU placeholder used by the descriptor must be listed here. A missing
   * required param makes renderNotificationText fall back to the server body, so
   * the UI never shows a leaked `{placeholder}` / "undefined".
   */
  requiredParams: string[]
}

export const EVENT_REGISTRY: Record<string, EventDef> = {
  order_created:           { descriptor: pushNotificationMessages.order_created,           requiredParams: ['order_id'] },
  order_canceled:          { descriptor: pushNotificationMessages.order_canceled,          requiredParams: ['order_id'] },
  item_canceled:           { descriptor: pushNotificationMessages.item_canceled,           requiredParams: ['order_id'] },
  order_updated:           { descriptor: pushNotificationMessages.order_updated,           requiredParams: ['order_id'] },
  order_status_incomplete: { descriptor: pushNotificationMessages.order_status_incomplete, requiredParams: ['order_id'] },
  order_not_delivered:     { descriptor: pushNotificationMessages.order_not_delivered,     requiredParams: ['order_id'] },
  order_acceptance_overdue:{ descriptor: pushNotificationMessages.order_acceptance_overdue, requiredParams: ['order_id'] },
}

export interface LocalizableMessage {
  descriptor: MessageDescriptor
  values: Record<string, string>
}

/**
 * Decide whether an envelope can be rendered client-side, independent of the
 * active locale. Returns the descriptor + values to format, or null to fall back
 * to the server body. The result is stored on the notification so the toast can
 * re-format reactively when the app language changes.
 *
 * null when: no envelope / unsupported schema_version / unknown event / missing
 * required param.
 */
export function resolveLocalizable(env: PushEnvelope | null): LocalizableMessage | null {
  if (!env) return null

  if (env.schemaVersion !== SUPPORTED_SCHEMA_VERSION) {
    console.warn(`[Push] unsupported schema_version "${env.schemaVersion}"; using server body`)
    return null
  }

  const def = EVENT_REGISTRY[env.event]
  if (!def) return null

  const missing = def.requiredParams.filter((param) => !env.params[param])
  if (missing.length > 0) {
    console.warn(`[Push] event "${env.event}" missing params [${missing.join(', ')}]; using server body`)
    return null
  }

  return { descriptor: def.descriptor, values: env.params }
}

/**
 * Render localized notification text from the typed envelope in the active app
 * locale, or return the server-rendered fallback body. Returns '' only when the
 * envelope can't be rendered AND no body was supplied (the tap path) — callers
 * should skip showing a banner in that case.
 */
export function renderNotificationText(env: PushEnvelope | null, body: string): string {
  const message = resolveLocalizable(env)
  if (!message) return body
  return IntlService.formatMessage(message.descriptor, message.values)
}
